import { useEffect, useRef } from 'react'
import { Search } from 'lucide-react'

export interface Suggestion {
  word: string
  wordArabic: string
}

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  autoFocus: boolean
  suggestions: Suggestion[]
  showSuggestions: boolean
  onSelectSuggestion: (word: string) => void
  onCloseSuggestions: () => void
}

export function SearchInput({
  value,
  onChange,
  autoFocus,
  suggestions,
  showSuggestions,
  onSelectSuggestion,
  onCloseSuggestions,
}: SearchInputProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        onCloseSuggestions()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onCloseSuggestions])

  return (
    <div className='relative w-full' ref={wrapperRef}>
      <Search className='absolute w-5 h-5 -translate-y-1/2 left-3 top-1/2 text-slate-400' />
      <input
        type='text'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder='دور على كلمة...'
        className='w-full h-10 pl-10 pr-4 border rounded-lg bg-slate-800 border-slate-700 text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent'
        autoFocus={autoFocus}
      />
      {showSuggestions && suggestions.length > 0 && (
        <SuggestionsList suggestions={suggestions} onSelect={onSelectSuggestion} />
      )}
    </div>
  )
}

interface SuggestionsListProps {
  suggestions: Suggestion[]
  onSelect: (word: string) => void
}

function SuggestionsList({ suggestions, onSelect }: SuggestionsListProps) {
  return (
    <div className='absolute z-50 w-full mt-1 overflow-hidden border rounded-lg shadow-lg bg-slate-800 border-slate-700'>
      {suggestions.map((suggestion) => (
        <button
          key={suggestion.word}
          type='button'
          onClick={() => onSelect(suggestion.word)}
          className='flex items-center justify-between w-full px-4 py-2 text-left transition-all duration-300 hover:bg-slate-700'>
          <span className='text-slate-100'>{suggestion.word}</span>
          <span className='text-sm text-slate-400'>{suggestion.wordArabic}</span>
        </button>
      ))}
    </div>
  )
}
